<template>
  <!-- 销售业绩：目标汇总/进度/趋势 + 业绩排行/明细 -->
  <div class="sale-performance">
    <list-page>
      <search-condition @search="queryAll">
        <template #default>
          <el-select v-model="query.year" placeholder="年度">
            <template #prefix>年度</template>
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
          <el-select v-model="query.businessType" placeholder="业务类型" @change="queryAll">
            <template #prefix>业务类型</template>
            <el-option v-for="b in businessTypes" :key="b.value" :label="b.label" :value="b.value"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="queryAll">查询</page-button>
        </template>
      </search-condition>
      <list-table>
        <div v-loading="loading" class="stat-body">
          <!-- 汇总：当前业务类型的年度目标/已达成/达成率/缺口 -->
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="label">年度目标</div>
              <div class="value">{{ metric(targetStat.annualTarget) }}<span class="unit">{{ unit }}</span></div>
              <div class="sub">{{ perf.businessName || "" }}</div>
            </div>
            <div class="kpi-card">
              <div class="label">已达成</div>
              <div class="value success">{{ metric(targetStat.annualAchieved) }}<span class="unit">{{ unit }}</span></div>
              <div class="sub">按当前业务类型实时统计</div>
            </div>
            <div class="kpi-card">
              <div class="label">达成率</div>
              <div class="value primary">{{ Number(targetStat.rate || 0).toFixed(1) }}<span class="unit">%</span></div>
              <div class="sub">已达成 / 年度目标</div>
            </div>
            <div class="kpi-card">
              <div class="label">缺口</div>
              <div class="value danger">{{ metric(targetStat.gap) }}<span class="unit">{{ unit }}</span></div>
              <div class="sub">年度目标 - 已达成</div>
            </div>
          </div>

          <!-- 目标进度 + 团队业绩趋势（同一个月度趋势，只保留一处） -->
          <div class="progress-trend-grid">
            <div class="chart-card">
              <div class="section-title">
                <span>目标进度</span>
                <span class="section-tip">
                点击季度区域可查看月度明细
                <table-button v-if="progressView" style="margin-left: 10px" @click="backAnnual">返回年度</table-button>
              </span>
              </div>
              <div id="performance-progress" class="pie-chart"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">
                <span>团队业绩趋势</span>
              </div>
              <div ref="trendChartDom" class="trend-chart"></div>
            </div>
          </div>

          <div class="grid-2">
            <!-- 业绩排行 TOP10：整行可点击 -->
            <div class="chart-card">
              <div class="section-title">业绩排行 TOP10</div>
              <div class="rank-list">
                <div v-for="(row, index) in rankRows" :key="'rank-' + row.employeeId" class="rank-item"
                     @click="openDrawer(row)">
                  <span class="rank-no" :class="'rank-' + (index + 1)">{{ index + 1 }}</span>
                  <span class="rank-name">{{ row.employeeName || "—" }}</span>
                  <span class="rank-sub">{{ row.recordCount || 0 }} {{ isAmountType ? "条" : "人" }}</span>
                  <span class="rank-val">{{ metric(row.achieved) }}</span>
                </div>
                <el-empty v-if="!rankRows.length" description="暂无业绩数据"/>
              </div>
            </div>

            <!-- 业绩明细：整行可点击 -->
            <div class="chart-card">
              <div class="section-title">业绩明细</div>
              <el-table :data="perf.rows || []" border height="320px" style="cursor: pointer"
                        @row-click="openDrawer">
                <el-table-column label="排名" width="60" align="center">
                  <template v-slot="{$index}">{{ $index + 1 }}</template>
                </el-table-column>
                <el-table-column label="销售人员" prop="employeeName" min-width="110" show-overflow-tooltip/>
                <el-table-column label="年度目标" prop="target" width="120" align="right">
                  <template v-slot="{row}">{{ metric(row.target) }}</template>
                </el-table-column>
                <el-table-column label="已达成" prop="achieved" width="120" align="right">
                  <template v-slot="{row}">{{ metric(row.achieved) }}</template>
                </el-table-column>
                <el-table-column label="达成率" prop="rate" width="100" align="center">
                  <template v-slot="{row}">{{ Number(row.rate || 0).toFixed(1) }}%</template>
                </el-table-column>
                <el-table-column label="缺口" prop="gap" width="120" align="right">
                  <template v-slot="{row}">{{ metric(row.gap) }}</template>
                </el-table-column>
                <el-table-column label="明细数" prop="recordCount" width="90" align="center"/>
                <el-table-column label="状态" prop="status" width="90" align="center">
                  <template v-slot="{row}">
                    <el-tag size="small" :type="statusTag(row.status)">{{ row.status }}</el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </list-table>
    </list-page>

    <!-- 业绩明细抽屉：用户/时间/业务类型可修改后重新查询 -->
    <el-drawer v-model="drawer.show" title="业绩明细" size="65%" :close-on-click-modal="false">
      <div class="drawer-body">
        <div class="drawer-filters">
          <employee-select v-model="drawer.employeeId" placeholder="全部销售人员">
            <template #prefix>用户</template>
          </employee-select>
          <el-date-picker v-model="drawer.dateRange" type="daterange" value-format="YYYY-MM-DD"
                          start-placeholder="开始日期" end-placeholder="结束日期" style="width: 260px"/>
          <el-select v-model="drawer.businessType" placeholder="业务类型">
            <template #prefix>业务类型</template>
            <el-option v-for="b in businessTypes" :key="b.value" :label="b.label" :value="b.value"/>
          </el-select>
          <page-button type="read" el-type="primary" @click="searchDetail">查询</page-button>
        </div>

        <!-- 合同明细 -->
        <el-table v-if="drawerIsAmountType" v-loading="drawer.loading" :data="drawer.dataList" border stripe>
          <el-table-column label="合同编号" prop="contractNumber" min-width="160" show-overflow-tooltip/>
          <el-table-column label="合同名称" prop="contractName" min-width="160" show-overflow-tooltip/>
          <el-table-column label="客户名称" prop="customerName" min-width="160" show-overflow-tooltip/>
          <el-table-column label="产品" prop="productName" min-width="150" show-overflow-tooltip/>
          <el-table-column label="销售金额" prop="amount" width="120" align="right">
            <template v-slot="{row}">{{ money(row.amount) }}</template>
          </el-table-column>
          <el-table-column label="成本" prop="cost" width="110" align="right">
            <template v-slot="{row}">{{ money(row.cost) }}</template>
          </el-table-column>
          <el-table-column label="毛利" prop="profit" width="110" align="right">
            <template v-slot="{row}">
              <span :style="{color: Number(row.profit) < 0 ? '#c0392b' : '#2e8b57'}">{{ money(row.profit) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="签约日期" prop="billDate" width="120" align="center">
            <template v-slot="{row}">{{ dateOnly(row.billDate) }}</template>
          </el-table-column>
          <el-table-column label="负责人" prop="employeeName" width="100" align="center">
            <template v-slot="{row}">{{ row.employeeName || "—" }}</template>
          </el-table-column>
        </el-table>

        <!-- 客户明细 -->
        <el-table v-else v-loading="drawer.loading" :data="drawer.dataList" border stripe>
          <el-table-column label="客户名称" prop="customerName" min-width="220" show-overflow-tooltip/>
          <el-table-column label="联系电话" prop="customerPhone" width="140" align="center"/>
          <el-table-column label="客户来源" prop="sourceDictId" width="140" align="center">
            <template v-slot="{row}">{{ dictName(sourceList, row.sourceDictId) || "—" }}</template>
          </el-table-column>
          <el-table-column label="创建时间" prop="createdTime" width="160" align="center">
            <template v-slot="{row}">{{ dateTime(row.createdTime) }}</template>
          </el-table-column>
          <el-table-column label="负责人" prop="employeeName" width="120" align="center">
            <template v-slot="{row}">{{ row.employeeName || "—" }}</template>
          </el-table-column>
        </el-table>

        <div class="drawer-pagination">
          <el-pagination background layout="total, prev, pager, next" :total="drawer.totalCount"
                         :page-size="drawer.pageSize" :current-page="drawer.pageNumber"
                         @current-change="onDrawerPage"/>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import saleTargetApi from "@/api/dz-crm/sale/saleTargetApi";
import commonApi from "@/api/commonApi";
import {DICT_PARTNER_ORIGIN, dictName} from "@/views/h5/crm/crmDictionary";
import ListTable from "@/components/h5/layout/ListTable.vue";

const QUARTER_COLORS = ["#1677ff", "#13b8b8", "#f09819", "#7a5af8"];
const MONTH_COLORS = ["#1677ff", "#13b8b8", "#f09819"];

export default {
  name: "SalePerformance",
  components: {ListTable, EmployeeSelect, TableButton, PageButton, SearchCondition, ListPage},
  data() {
    const year = new Date().getFullYear();
    return {
      yearOptions: Array.from({length: 7}, (_, i) => year - 5 + i),
      businessTypes: [
        {value: 1, label: "毛利"},
        {value: 2, label: "新增客户"},
        {value: 3, label: "客户转介绍"},
        {value: 4, label: "服务费"},
      ],
      query: {year, businessType: 1},
      perf: {targetStat: {}, rows: []},
      loading: false,
      progressView: 0, // 0=年度，1-4=季度
      sourceList: [],
      drawer: {
        show: false,
        employeeId: "",
        businessType: 1,
        dateRange: [],
        pageNumber: 1,
        pageSize: 10,
        totalCount: 0,
        dataList: [],
        loading: false,
      },
    };
  },
  computed: {
    targetStat() {
      return this.perf.targetStat || {};
    },
    unit() {
      return this.perf.unit || "元";
    },
    isAmountType() {
      return [1, 4].includes(Number(this.query.businessType));
    },
    dashboardIsAmountType() {
      return [1, 4].includes(Number(this.perf.businessType || this.query.businessType));
    },
    drawerIsAmountType() {
      return [1, 4].includes(Number(this.drawer.businessType));
    },
    rankRows() {
      return (this.perf.rows || []).slice(0, 10);
    },
  },
  created() {
    this.progressChart = null;
    this.trendChartInstance = null;
    commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN).then(list => this.sourceList = list || []);
    this.queryAll();
  },
  unmounted() {
    this.disposeCharts();
  },
  methods: {
    dictName,
    queryAll() {
      this.loading = true;
      this.progressView = 0;
      saleTargetApi.performance({year: this.query.year, businessType: this.query.businessType}).then(res => {
        this.perf = res || {targetStat: {}, rows: []};
        this.$nextTick(() => this.renderCharts());
      }).finally(() => {
        this.loading = false;
      });
    },
    disposeCharts() {
      if (this.progressChart) {
        this.progressChart.dispose();
        this.progressChart = null;
      }
      if (this.trendChartInstance) {
        this.trendChartInstance.dispose();
        this.trendChartInstance = null;
      }
    },
    renderCharts() {
      this.disposeCharts();
      this.renderProgressChart();
      this.renderTrendChart();
    },
    // ---------- 目标进度饼图 ----------
    renderProgressChart() {
      const dom = document.getElementById("performance-progress");
      if (!dom) {
        return;
      }
      const data = this.buildPieData();
      // 下钻/返回年度时复用同一个实例；只有首次初始化或整页重查（dispose 后）才重新 init
      if (!this.progressChart) {
        this.progressChart = markRaw(echarts.init(dom));
      }
      this.progressChart.setOption({
        tooltip: {
          trigger: "item",
          appendToBody: true,
          formatter: p => {
            const d = p.data || {};
            return `${d.periodName || ""} ${d.status || ""}<br/>${this.metric(d.value)} ${this.unit}`
                + `<br/>目标：${this.metric(d.target)} / 已达成：${this.metric(d.achieved)}`;
          },
        },
        title: {
          text: data.length ? "" : "暂无目标",
          subtext: data.length ? "" : "请先维护销售目标",
          left: "center",
          top: "42%",
          textStyle: {fontSize: 14, color: "#909399"},
          subtextStyle: {fontSize: 11, color: "#c0c4cc"},
        },
        series: [{
          type: "pie",
          radius: ["32%", "60%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          minAngle: 2,
          label: {show: false},
          labelLine: {length: 6, length2: 6, lineStyle: {color: "#c0c4cc"}},
          labelLayout: {hideOverlap: true},
          data,
        }],
      }, true);
      this.progressChart.off("click");
      this.progressChart.on("click", p => {
        if (!this.progressView && p.data && p.data.quarter) {
          this.progressView = p.data.quarter;
          this.renderProgressChart();
        }
      });
    },
    buildPieData() {
      const data = [];
      const stat = this.targetStat;
      if (!this.progressView) {
        (stat.quarters || []).forEach((q, index) => {
          const color = QUARTER_COLORS[index % QUARTER_COLORS.length];
          this.pushPiePair(data, q, "第" + this.quarterText(q.period) + "季度", color, q.period,
              "Q" + q.period);
        });
      } else {
        (stat.months || [])
          .filter(m => Math.ceil(m.period / 3) === this.progressView)
          .forEach((m, index) => {
            const color = MONTH_COLORS[index % MONTH_COLORS.length];
            this.pushPiePair(data, m, m.name, color, this.progressView, m.period + "月");
          });
      }
      return data;
    },
    pushPiePair(data, period, periodName, color, quarter, labelPrefix) {
      const completed = Number(period.completed) || 0;
      const uncompleted = Number(period.uncompleted) || 0;
      const rateLabel = {
        show: true,
        fontSize: 9,
        color: "#606266",
        lineHeight: 12,
        formatter: labelPrefix + " " + Number(period.rate || 0).toFixed(1) + "%",
      };
      const hideLabel = {show: false};
      if (completed > 0) {
        data.push({
          name: periodName + " 已完成", value: completed, quarter, periodName, status: "已完成",
          target: Number(period.target || 0), achieved: Number(period.achieved || 0),
          itemStyle: {color}, label: rateLabel,
        });
      }
      if (uncompleted > 0) {
        data.push({
          name: periodName + " 未完成", value: uncompleted, quarter, periodName, status: "未完成",
          target: Number(period.target || 0), achieved: Number(period.achieved || 0),
          itemStyle: {color: this.hexToRgba(color, 0.25)},
          label: completed > 0 ? hideLabel : rateLabel,
        });
      }
    },
    backAnnual() {
      this.progressView = 0;
      this.renderProgressChart();
    },
    // ---------- 月度趋势 ----------
    renderTrendChart() {
      const dom = this.$refs.trendChartDom;
      if (!dom) {
        return;
      }
      const months = this.targetStat.months || [];
      this.trendChartInstance = markRaw(echarts.init(dom));
      this.trendChartInstance.setOption({
        tooltip: {trigger: "axis", appendToBody: true},
        legend: {data: ["目标", "实际"], top: 0, textStyle: {color: "#606266"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "16%", containLabel: true},
        xAxis: {type: "category", data: months.map(m => m.name), axisLabel: {color: "#909399"}},
        yAxis: {type: "value", name: this.unit, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [
          {
            name: "目标", type: "line", smooth: true, symbol: "circle", symbolSize: 5,
            lineStyle: {color: "#c3ccd9"}, itemStyle: {color: "#c3ccd9"},
            data: months.map(m => Number(m.target || 0)),
          },
          {
            name: "实际", type: "line", smooth: true, symbol: "circle", symbolSize: 5,
            lineStyle: {color: "#1677ff"}, itemStyle: {color: "#1677ff"}, areaStyle: {opacity: 0.08},
            data: months.map(m => Number(m.achieved || 0)),
          },
        ],
      }, true);
    },
    // ---------- 业绩明细抽屉 ----------
    openDrawer(row) {
      const year = this.query.year;
      this.drawer.employeeId = row ? row.employeeId : "";
      this.drawer.businessType = this.query.businessType;
      this.drawer.dateRange = [`${year}-01-01`, `${year}-12-31`];
      this.drawer.pageNumber = 1;
      this.drawer.show = true;
      this.loadDetail();
    },
    searchDetail() {
      this.drawer.pageNumber = 1;
      this.loadDetail();
    },
    onDrawerPage(page) {
      this.drawer.pageNumber = page;
      this.loadDetail();
    },
    loadDetail() {
      const range = this.drawer.dateRange || [];
      this.drawer.loading = true;
      saleTargetApi.performanceDetail({
        year: this.query.year,
        employeeId: this.drawer.employeeId,
        businessType: this.drawer.businessType,
        startTime: range[0] || null,
        endTime: range[1] || null,
        pageNumber: this.drawer.pageNumber,
        pageSize: this.drawer.pageSize,
      }).then(res => {
        this.drawer.dataList = res.records || [];
        this.drawer.totalCount = res.totalCount || 0;
      }).finally(() => {
        this.drawer.loading = false;
      });
    },
    // ---------- 展示 ----------
    metric(value) {
      if (value === null || value === undefined || value === "") {
        return this.unit === "个" ? "0" : "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {
        minimumFractionDigits: this.unit === "个" ? 0 : 2,
        maximumFractionDigits: this.unit === "个" ? 0 : 2,
      });
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 19).replace("T", " ") : "—";
    },
    quarterText(q) {
      return ["一", "二", "三", "四"][Number(q) - 1] || "";
    },
    statusTag(status) {
      if (status === "已达标") return "success";
      if (status === "进行中") return "primary";
      return "warning";
    },
    hexToRgba(hex, alpha) {
      const value = hex.replace("#", "");
      const r = parseInt(value.substring(0, 2), 16);
      const g = parseInt(value.substring(2, 4), 16);
      const b = parseInt(value.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style lang="scss" scoped>
.sale-performance {
  height: 100%;

  .stat-body {
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }

  .kpi-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 8px;
    padding: 14px 16px;
    background: #fff;

    .label {
      font-size: 13px;
      color: #909399;
    }

    .value {
      margin-top: 6px;
      font-size: 22px;
      font-weight: 700;
      color: #1f2329;

      &.success { color: #2e8b57; }
      &.primary { color: #1677ff; }
      &.danger { color: #e5484d; }
    }

    .unit {
      margin-left: 4px;
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }

    .sub {
      margin-top: 4px;
      font-size: 12px;
      color: #b0b6c0;
    }
  }

  .progress-trend-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: stretch;
  }

  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .chart-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fff;
  }

  .section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid var(--el-color-primary);

    .section-tip {
      display: flex;
      align-items: center;
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }

  .pie-chart {
    height: 300px;
  }

  .trend-chart {
    flex: 1;
    min-height: 300px;
  }

  .rank-list {
    flex: 1;
    overflow-y: auto;
  }

  .rank-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 8px;
    border-bottom: 1px solid #f2f3f5;
    cursor: pointer;

    &:hover {
      background: #f8fafc;
    }

    .rank-no {
      flex: none;
      width: 22px;
      height: 22px;
      line-height: 22px;
      text-align: center;
      border-radius: 50%;
      background: #f0f2f5;
      color: #909399;
      font-size: 12px;

      &.rank-1 { background: #f6c344; color: #fff; }
      &.rank-2 { background: #b9c3cf; color: #fff; }
      &.rank-3 { background: #d9a066; color: #fff; }
    }

    .rank-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #303133;
      font-weight: 500;
    }

    .rank-sub {
      flex: none;
      color: #909399;
      font-size: 12px;
    }

    .rank-val {
      flex: none;
      color: #1677ff;
      font-weight: 600;
    }
  }

  .drawer-body {
    padding: 0 4px;
  }

  .drawer-filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .drawer-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }

  @media (max-width: 1400px) {
    .progress-trend-grid,
    .grid-2 {
      grid-template-columns: 1fr;
    }
    .kpi-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
